// Display Name Validation Class
// ------------------------------
// Pre-flight availability check for a preferred username.
//
// The backend answer is pool-wide, because that is what Cognito enforces, and
// is rate limited for exactly that reason. So this call has three outcomes, not
// two: available, taken, or "we could not tell you". The third one must never
// be reported as available — the user would fill in a name the save then
// rejects. Every path here resolves to a message; nothing throws at the caller.

import { getGraphData } from "@/utils/transform";
import { API, graphqlOperation as _graphqlOperation } from "aws-amplify";
import { assertNoTenantIdInVariables } from "@/services/graphql/assertNoTenantId";

import {
  EMPTY_STRING,
  BACKEND_CODE_RATE_LIMITED,
  BACKEND_CODE_INTERNAL_ERROR,
} from "../constants/definitions";
import { doesDisplayNameExist } from "../services/graphql/queries";

// Force Cognito User Pools auth on every GraphQL call.
const graphqlOperation = (query: any, variables?: any) => {
  // Reject any client-side tenantId leak before the request leaves
  // the wrapper. See src/services/graphql/assertNoTenantId.ts for
  // the contract — symmetric to the BE rule "never accept tenantId
  // from client input."
  assertNoTenantIdInVariables(variables);
  return {
    ..._graphqlOperation(query, variables),
    authMode: "AMAZON_COGNITO_USER_POOLS" as const,
  };
};

const ERROR_DISPLAY_NAME_ALREADY_USED =
  "This preferred username has already been registered, choose a different one";
const ERROR_TOO_MANY_CHECKS =
  "Too many availability checks. Please wait a few minutes and try again.";
const ERROR_COULD_NOT_CHECK =
  "Could not check whether that preferred username is available. Please try again.";

// Sentinel codes this call is allowed to recognise. Matching against an
// allowlist rather than reading the raw message keeps backend or transport
// detail (resolver internals, endpoints, stack text) out of both the UI and
// the browser console — a rejection is attacker-influenceable input.
const RECOGNISED_BACKEND_CODES: readonly string[] = [
  BACKEND_CODE_RATE_LIMITED,
  BACKEND_CODE_INTERNAL_ERROR,
];

// Reduce an Amplify GraphQL rejection to a known code, or "" if it is not one
// we recognise. The resolver renders sentinels via $util.error, so the code
// arrives as the error message rather than as data.
function extractBackendCode(caughtError: any): string {
  const rawMessage = caughtError?.errors?.[0]?.message || caughtError?.message || "";
  return RECOGNISED_BACKEND_CODES.includes(rawMessage) ? rawMessage : "";
}

class DisplayNameValidationClass {
  // Validate Display Name
  // -----------------------
  // Resolves to an empty string when the name is free, or to a user-facing
  // message when it is not (or when we could not find out).
  async validateDisplayName(
    displayName: string,
    currentError: string
  ): Promise<string> {
    // If the current error is blank such that no error previously set will be over written
    // Or if the current error is set by us such that we can change it
    // Then check again
    if (
      EMPTY_STRING !== currentError &&
      ERROR_DISPLAY_NAME_ALREADY_USED !== currentError
    ) {
      // Not fit to evaluate the error - return the current one so it doesn't change state
      return currentError;
    }

    try {
      const response = await API.graphql(
        graphqlOperation(doesDisplayNameExist, { handle: displayName })
      );
      const nameIsTaken = getGraphData(response, "doesDisplayNameExist");
      return nameIsTaken ? ERROR_DISPLAY_NAME_ALREADY_USED : EMPTY_STRING;
    } catch (availabilityCheckError: any) {
      const backendCode = extractBackendCode(availabilityCheckError);
      if (backendCode === BACKEND_CODE_RATE_LIMITED) {
        return ERROR_TOO_MANY_CHECKS;
      }
      // INTERNAL_ERROR, an unauthorized response, or a transport failure. Report
      // it rather than letting the rejection escape as an unhandled promise,
      // and never fall through to "available". The log carries the recognised
      // code only, never the raw message.
      console.error(
        `doesDisplayNameExist failed${backendCode ? ` (${backendCode})` : ""}`
      );
      return ERROR_COULD_NOT_CHECK;
    }
  }
}

export default DisplayNameValidationClass;
export { ERROR_DISPLAY_NAME_ALREADY_USED };
